/**
 * Pillar F.5 (owner ruling 2026-08-20) — the rendered state of ONE evidence body that was fetched
 * because the operator asked for it.
 *
 * Presentational only, and shared by all three surfaces that render evidence rows (the CRM Evidence
 * sub-tab, the E1a consolidated record timeline, the Deals tab card) so the three cannot drift into
 * three different loading/error stories over the same content. Each surface owns its OWN query — the
 * CRM ones through the governed, audited `…/evidence/{id}/body` door, the Deals tab through its
 * `/deal-cards/…` twin — because the lanes are gated separately and must stay that way.
 *
 * THE FETCH DISCIPLINE IS STRUCTURAL, not a flag: this component is mounted only after a click, and
 * whoever mounts it starts the request. Nothing here (and nothing above it) prefetches, fetches on
 * hover, or fetches on render — that is the ruling, and mounting-on-click is how it is enforced rather
 * than merely intended.
 *
 * `data-evidence-body` is the tested contract (CSS-module class names are hashed, so tests assert on
 * the data attribute): "loading" | "error" | "ready".
 */
export type EvidenceBodyState = 'loading' | 'error' | 'ready';

export function EvidenceBody({
  state, body, className,
}: {
  state: EvidenceBodyState;
  /** The text, once it has arrived. Ignored in the other two states. */
  body?: string | null;
  /** The host surface's own body style (each has its own CSS module). */
  className?: string;
}) {
  if (state === 'loading') {
    return (
      <div className={className} data-evidence-body="loading" role="status">
        Loading the message…
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className={className} data-evidence-body="error" role="alert">
        Couldn’t load this message.
      </div>
    );
  }
  return (
    <div className={className} data-evidence-body="ready">
      {body ?? ''}
    </div>
  );
}

/** react-query's three flags → the one state this renders from. Pending-and-enabled is "loading";
 * an error wins over stale data because a body we could not re-read is not a body we should show. */
export function evidenceBodyState(
  q: { isLoading: boolean; isError: boolean; isPending?: boolean },
): EvidenceBodyState {
  if (q.isError) return 'error';
  if (q.isLoading || q.isPending) return 'loading';
  return 'ready';
}

export default EvidenceBody;
